import { AnalyticsEvent, AnalyticsService } from 'src/app/core/services/analytics-service';
import { Notification, Notifications, NotificationStatus } from '../models/notification.model';
import { ApiService } from '../../core/services/api.service';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { NotificationState } from '../models/notification-state.model';
import { Store } from 'src/app/core/services/store.service';
import { CustomerStoreService } from 'src/app/core/services/customer-store.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationStoreService extends Store<NotificationState> implements OnDestroy {
  private unsubscribe = new Subject();
  public count = new Subject<number>();
  public newNotifications = new Subject<boolean>();

  constructor(
    private api: ApiService,
    private customerStoreService: CustomerStoreService,
    private analyticsService: AnalyticsService,
  ) {
    super(null);
    this.api
      .subscribe<Notification>('notifications/customer')
      .pipe(
        startWith(''),
        takeUntil(this.unsubscribe),
        tap((socketFrame) => this.setNotificationState(this.matcher(this.state, socketFrame))),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  get notifications(): Notification[] {
    return this.state?.notifications || [];
  }

  private get nextPageNumber(): number {
    if (this.state) {
      return ++this.state.pageNumber;
    } else {
      return 0;
    }
  }

  public init(): Observable<NotificationState> {
    this.setNotificationState(null);
    return this.loadNextPage();
  }

  private setNotificationState(nextState: NotificationState): void {
    this.setState(nextState);
    if (nextState !== null) {
      this.count.next(nextState.unreadCount);
    }
  }

  public loadNextPage(): Observable<NotificationState> {
    return this.mapPageResultToNotification(this.nextPageNumber);
  }

  private mapPageResultToNotification(pageNumber: number): Observable<NotificationState> {
    return this.getAllForCustomer(pageNumber).pipe(
      map((notificationsVM: Notifications) => {
        const page = notificationsVM.notifications;
        const hasElements = page && page.numberOfElements > 0;

        const state = hasElements ? this.mergeWithPreviousState(notificationsVM) : this.setEmptyState(notificationsVM);
        this.setNotificationState(state);


        return state;
      }),
      tap(() => {
        if (this.state.notifications && this.state.notifications.length > 0
          && (this.state.lastReadAt < this.state.notifications[0].createdAt)) {
          this.newNotifications.next(true);
        }
      }),
    );
  }

  public getAllForCustomer(pageNumber: number): Observable<Notifications> {
    const { customerId } = this.customerStoreService.state;
    return this.api.get<Notifications>(`/notifications/customer/${customerId}`, {
      page: `${pageNumber}`,
    });
  }

  public changeReadStatus(notificationId: string, status: NotificationStatus): Observable<number> {
    return this.api.put<number>(`/notifications/read-status/${status}`, {}, { notificationIds: [notificationId] }).pipe(
      tap((unreadCount: number) => {
        this.updateUnreadCountAndStatus(unreadCount, notificationId, status);
        this.analyticsService.trackEvent(new AnalyticsEvent(`Notification marked as ${status}`, { notificationId }));
      }),
    );
  }

  public changeAllReadStatus(status: NotificationStatus): Observable<any> {
    return this.api.put<number>(`/notifications/read-status/${status}/all`).pipe(
      switchMap(() => {
        this.analyticsService.trackEvent(new AnalyticsEvent(`All notifications marked as ${status}`));
        if (status === NotificationStatus.READ) {
          return of(this.updateUnreadCountAndStatus(0, null, status));
        } else {
          return this.loadNextPage().pipe(take(1));
        }
      }),
    );
  }

  private updateUnreadCountAndStatus(unreadCount: number, notificationId: string, status: NotificationStatus) {
    this.state.unreadCount = unreadCount;
    this.count.next(unreadCount);
    if (!notificationId) {
      return;
    }
    if (NotificationStatus.REMOVED === status) {
      this.state.notifications = this.state.notifications
        .filter((notification: Notification) => notification.notificationId !== notificationId);
    } else {
      this.state.notifications
        .filter((notification: Notification) => notification.notificationId === notificationId)
        .map((notification: Notification) => (notification.status = status));
    }
  }

  private mergeWithPreviousState(notificationsVM: Notifications): NotificationState {
    const page = notificationsVM.notifications;

    let state: NotificationState = {
      notifications: page.content,
      pageNumber: page.number,
      last: page.last,
      unreadCount: notificationsVM.unreadMessagesCount,
      lastReadAt: notificationsVM.lastReadAt ? notificationsVM.lastReadAt : this.state?.lastReadAt ? this.state?.lastReadAt : null,
    };

    if (this.notifications.length > 0) {
      state.notifications = this.state.notifications.concat(state.notifications);
    }

    return state;
  }

  private setEmptyState(notificationsVM: Notifications): NotificationState {
    let notificationState: NotificationState;

    if (!this.state) {
      notificationState = {
        notifications: [],
        last: true,
        pageNumber: 0,
        unreadCount: notificationsVM.unreadMessagesCount,
        lastReadAt: notificationsVM.lastReadAt,
      };

    } else if (this.state && this.notifications.length > 0) {
      notificationState = this.state;
      notificationState.unreadCount = notificationsVM.unreadMessagesCount;
    }

    return notificationState;
  }

  private matcher(data, socketFrame) {
    if (socketFrame) {
      const notifications = [socketFrame, ...data.notifications];
      const unreadCount = ++data.unreadCount;

      const notificationState = {
        notifications,
        unreadCount,
      };

      return notificationState;
    }
    return data;
  }

}
